import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useGiveawayCategory } from '@/hooks/queries'
import { useOverlayStore } from '@/store/overlays'
import { useSearchStore } from '@/store/search'
import type { Giveaway } from '@/models/giveaway'
import { GiveawayFeed } from '@/components/giveaway/GiveawayFeed'
import { cn } from '@/lib/cn'
import {
  filterOptions,
  isGiveawaySort,
  matchesFilters,
  searchGiveaways,
  SORT_LABELS,
  sortGiveaways,
  type GiveawaySort,
  type FilterOption,
} from '@/lib/giveaway-search'

/**
 * Port of pages/Search.vue.
 *
 * That page was never routed and rendered eight hard-coded "Spawn Pre-Alpha
 * Access" cards from `results: [1..8]`, with inert genre and sort dropdowns.
 * The layout is kept, wired to a client-side filter over the giveaway list —
 * the API has no giveaway-search endpoint, only /search/game_info/autocomplete,
 * so this is as far as it can honestly go without a backend change.
 */
export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const query = searchParams.get('q') ?? ''

  const clearSearch = useSearchStore((state) => state.clear)
  const setSelectedGiveaway = useOverlayStore((state) => state.setSelectedGiveaway)
  const openOverlay = useOverlayStore((state) => state.open)

  const { data: giveaways = [], isPending } = useGiveawayCategory('all')

  const genre = searchParams.get('genre') || null
  const platform = searchParams.get('platform') || null
  const filtered = genre !== null || platform !== null

  // Best match only means something with a query; without one, newest first.
  const defaultSort: GiveawaySort = query.trim() ? 'relevance' : 'newest'
  const requestedSort = searchParams.get('sort')
  const sort: GiveawaySort =
    isGiveawaySort(requestedSort) && (requestedSort !== 'relevance' || query.trim())
      ? requestedSort
      : defaultSort
  const sortChoices = (Object.keys(SORT_LABELS) as GiveawaySort[]).filter(
    (option) => option !== 'relevance' || query.trim(),
  )

  // Filters live in the URL beside the query, so a filtered search can be
  // shared or bookmarked and survives a reload.
  // One call per change: React Router doesn't chain updates made in a row.
  const setParams = (
    changes: Partial<Record<'q' | 'genre' | 'platform' | 'sort', string | null>>,
  ) => {
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params)
        for (const [key, value] of Object.entries(changes)) {
          if (value) next.set(key, value)
          else next.delete(key)
        }
        return next
      },
      { replace: true },
    )
  }

  const searched = useMemo(() => searchGiveaways(giveaways, query), [giveaways, query])
  const results = useMemo(
    () =>
      sortGiveaways(
        searched.filter((giveaway) => matchesFilters(giveaway, { genre, platform })),
        sort,
      ),
    [searched, genre, platform, sort],
  )
  // Each list counts what picking an option would leave, given the search and
  // the other filter.
  const genreOptions = useMemo(
    () =>
      filterOptions(
        searched.filter((g) => matchesFilters(g, { genre: null, platform })),
        'genres',
        genre,
      ),
    [searched, platform, genre],
  )
  const platformOptions = useMemo(
    () =>
      filterOptions(
        searched.filter((g) => matchesFilters(g, { genre, platform: null })),
        'platforms',
        platform,
      ),
    [searched, genre, platform],
  )

  const select = (giveaway: Giveaway) => {
    setSelectedGiveaway(giveaway)
    openOverlay('giveaway-details')
  }

  return (
    <div className="search">
      <Link className="back-link" to="/">
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          aria-hidden="true"
        >
          <path d="M9.5 3.5 5 8l4.5 4.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Back to home
      </Link>

      <div className="search-results-text">
        {query ? `Search results for '${query}'` : 'All giveaways'}
        {query ? (
          <button
            type="button"
            className="search-results-clear"
            aria-label="Clear search"
            onClick={() => {
              clearSearch()
              setParams({ q: null })
            }}
          >
            <img src="/icons/close.png" alt="" />
          </button>
        ) : null}
      </div>

      <div className="search-filters" role="group" aria-label="Filter giveaways">
        <FilterSelect
          label="Genre"
          allLabel="All genres"
          value={genre}
          options={genreOptions}
          onChange={(value) => setParams({ genre: value })}
        />
        <FilterSelect
          label="Platform"
          allLabel="All platforms"
          value={platform}
          options={platformOptions}
          onChange={(value) => setParams({ platform: value })}
        />
        <label className="search-filter search-sort">
          <span className="search-filter-label">Sort</span>
          <select
            className="search-filter-select"
            value={sort}
            onChange={(event) => {
              const next = event.target.value as GiveawaySort
              setParams({ sort: next === defaultSort ? null : next })
            }}
          >
            {sortChoices.map((option) => (
              <option key={option} value={option}>
                {SORT_LABELS[option]}
              </option>
            ))}
          </select>
        </label>
        {filtered ? (
          <button
            type="button"
            className="link-button search-filters-clear"
            onClick={() => {
              setParams({ genre: null, platform: null })
            }}
          >
            Clear filters
          </button>
        ) : null}
        {!isPending ? (
          <span className="search-filters-count" aria-live="polite">
            {results.length} {results.length === 1 ? 'giveaway' : 'giveaways'}
          </span>
        ) : null}
      </div>

      <GiveawayFeed
        giveaways={results}
        isPending={isPending}
        onSelect={select}
        skeletonCount={3}
        emptyTitle="No matches"
        emptyBody={
          filtered
            ? 'Nothing matches these filters. Try another genre or platform, or clear the filters.'
            : query
              ? `Nothing matched ‘${query}’. Try a different game or creator.`
              : 'No giveaways are running right now.'
        }
      />
    </div>
  )
}

function FilterSelect({
  label,
  allLabel,
  value,
  options,
  onChange,
}: {
  label: string
  allLabel: string
  value: string | null
  options: FilterOption[]
  onChange: (value: string | null) => void
}) {
  return (
    <label className={cn('search-filter', value && 'active')}>
      <span className="search-filter-label">{label}</span>
      <select
        className="search-filter-select"
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value || null)}
      >
        <option value="">{allLabel}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.value} ({option.count})
          </option>
        ))}
      </select>
    </label>
  )
}
